import { useQueries } from "@tanstack/react-query";
import { mediumRepository } from "../repositories/mediumRepository";
import { profileRepository } from "../repositories/profileRepository";

export const profileQueryKeys = {
  profile: ["profile"] as const,
  // /profile/me returns a different medium per mediumId, so each one needs its own cache entry.
  details: (mediumId?: string) =>
    [...profileQueryKeys.profile, "details", mediumId ?? "default"] as const,
  mediumStatus: ["medium", "me", "status"] as const,
};

export const useProfileQuery = (mediumId?: string) => {
  const queries = useQueries({
    queries: [
      {
        queryKey: profileQueryKeys.details(mediumId),
        queryFn: () =>
          profileRepository.getProfile({
            includeUser: true,
            showProfileId: true,
            includeAddress: true,
            includeContact: true,
            includeMedium: true,
            ...(mediumId ? { mediumId } : {}),
          }),
        staleTime: 1000 * 60 * 15, // 15 minutes - profile changes infrequently
        gcTime: 60 * 60 * 1000, // Cache for 1 hour
        retry: 3,
        refetchOnMount: "always", // <-- This forces refetch on every mount
      },
      {
        queryKey: [...profileQueryKeys.mediumStatus, mediumId ?? "default"],
        queryFn: () => mediumRepository.getMyStatus(mediumId),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 60 * 60 * 1000, // 1 hour
        retry: 2,
        refetchOnMount: "always",
      },
    ],
  });

  const [profileQuery, mediumStatusQuery] = queries;

  return {
    // Data
    data: profileQuery.data,
    mediumStatus: mediumStatusQuery.data,

    // Loading state
    isLoading: profileQuery.isLoading,
    isMediumStatusLoading: mediumStatusQuery.isLoading,

    // Error state
    error: profileQuery.error,
    mediumStatusError: mediumStatusQuery.error,

    // Actions
    refetchProfile: profileQuery.refetch,
    refetchMediumStatus: mediumStatusQuery.refetch,

    // Status
    isSuccess: profileQuery.isSuccess,
    isMediumStatusSuccess: mediumStatusQuery.isSuccess,
    isError: profileQuery.isError,
    isMediumStatusError: mediumStatusQuery.isError,
  };
};
